import { formatInTimeZone } from "date-fns-tz";
import { ProductCodesMap } from "@/types/constants/productCodes";
import { ProductTier } from "@/types/interfaces/crm";

const PRODUCT_TIER_FIELD_KEYWORDS: { field: keyof ProductTier; keywords: string[] }[] = [
  { field: "insuranceTierName", keywords: ["INSURANCE"] },
  { field: "feeTierName", keywords: ["FEE"] },
  { field: "cruiseTierName", keywords: ["CRUISE"] },
  { field: "hotelTierName", keywords: ["HOTEL"] },
  { field: "carTierName", keywords: ["CAR"] },
  { field: "transportationTierName", keywords: ["TRANSFER", "RAIL", "TRANSPORT"] },
  {
    field: "tourTierName",
    keywords: ["TOUR", "PACKAGE", "GUIDED", "PARKS AND ATTRACTIONS", "ITC", "FIT", "DAY"],
  },
  { field: "airTierName", keywords: ["AIR", "B.S.P", "BSP", "CONSOLIDATOR", "SEAT SELECTION", "NON ARC"] },
  { field: "miscTierName", keywords: ["MISC"] },
];

export function resolveProductTier(
  productTierObj: ProductTier | undefined,
  productCode: string | number | undefined | null,
): string {
  if (!productTierObj) return "";

  const description = (ProductCodesMap[String(productCode)] || "").toUpperCase();
  if (description) {
    for (const { field, keywords } of PRODUCT_TIER_FIELD_KEYWORDS) {
      const tierName = productTierObj[field];
      if (tierName && keywords.some((keyword) => description.includes(keyword))) {
        return tierName;
      }
    }
  }

  return productTierObj.productTier || "";
}

const POSTAL_CODE_REGION_MAP = {
  A: "NL",
  B: "NS",
  C: "PE",
  E: "NB",

  G: "QC",
  H: "QC",
  J: "QC",

  K: "ON",
  L: "ON",
  M: "ON",
  N: "ON",
  P: "ON",

  R: "MB",
  S: "SK",
  T: "AB",
  V: "BC",

  X: "NT/NU",
  Y: "YT",
};

const POSTAL_CODE_REGEX = /\b([A-Z])\d[A-Z][ ]?\d[A-Z]\d\b/i;

const REGION_CODE_REGEX = /\b(BC|ON|AB|QC|MB|SK|NS|NB|NL|PE|YT|NT|NU)\b/i;

export function extractCanadaRegion(addressText: string) {
  const text = addressText.toUpperCase();

  let postalRegion = null;
  let explicitRegion = null;

  const postalMatch = text.match(POSTAL_CODE_REGEX);
  if (postalMatch) {
    const firstLetter = postalMatch[1] as keyof typeof POSTAL_CODE_REGION_MAP;
    postalRegion = POSTAL_CODE_REGION_MAP[firstLetter] || null;
  }

  const regionMatch = text.match(REGION_CODE_REGEX);
  if (regionMatch) {
    explicitRegion = regionMatch[1];
  }

  if (postalRegion && explicitRegion) {
    if (postalRegion === explicitRegion || postalRegion.includes(explicitRegion)) {
      return {
        region: explicitRegion,
        confidence: "VERY_HIGH",
        source: "postal_code + explicit",
      };
    } else {
      return {
        region: null,
        confidence: "ZERO",
        source: "conflict",
        error: "Postal code and region do not match",
      };
    }
  }

  if (postalRegion) {
    return {
      region: postalRegion,
      confidence: postalRegion === "NT/NU" ? "MEDIUM" : "HIGH",
      source: "postal_code",
    };
  }

  if (explicitRegion) {
    return {
      region: explicitRegion,
      confidence: "MEDIUM",
      source: "explicit",
    };
  }

  return {
    region: null,
    confidence: "NONE",
    source: "unknown",
  };
}

export function regexFromString(regexString: string): RegExp {
  const match = regexString.match(/^\/(.+)\/([a-z]*)$/i);
  if (!match) {
    throw new Error("Invalid regex string");
  }
  return new RegExp(match[1], match[2]);
}

export const formatInTorontoTime = (date: Date | null | undefined, formatStr: string): string => {
  if (!date || isNaN(date.getTime())) {
    return "_";
  }
  return formatInTimeZone(date, "UTC", formatStr);
};
